import type {
  ClientPoll,
  ClientPollCreateForPostRequest,
  ClientPollCreateRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';

import type {
  PollOption,
  PollCreate,
  Poll,
  PollVote
} from '@/features/post-editor/types/poll';

const OPTION_TEXT_KEY = 'optionText';

export const getPollOptions = (poll?: Partial<ClientPoll>): PollOption[] => {
  const options: PollOption[] = [];

  const voterCount = poll?.voterCount;
  const count = poll?.multipleChoice ? (poll?.numberOfOptions ?? 2) : 1;

  if (!poll) return [];

  for (const key of Object.keys(poll)) {
    if (key.startsWith(OPTION_TEXT_KEY)) {
      const optionText = (
        (poll[key as keyof ClientPoll] as string) ?? ''
      ).trim();

      if (optionText) {
        // Extract option number (e.g., "optionText1" -> "1")
        const optionNumber = key.replace(OPTION_TEXT_KEY, '');

        const votesKey = `option${optionNumber}Votes` as keyof ClientPoll;
        const votedKey = `pollOption${optionNumber}Voted` as keyof ClientPoll;

        const votes = (poll[votesKey] as number) || 0;
        const voted = (poll[votedKey] as boolean) || false;
        const percentage = voterCount
          ? (votes / (count * voterCount)) * 100
          : 0;

        options.push({
          optionText,
          votes,
          voted,
          percentage
        });
      }
    }
  }

  return options;
};

export const convertPollCreateToClientPoll = (
  poll: PollCreate
): ClientPollCreateRequest => ({
  text: poll.text,
  multipleChoice: poll.multipleChoice,
  expirationTime: poll.expirationTime,
  optionText1: poll.optionTexts[0].optionText.trim(),
  optionText2: poll.optionTexts[1].optionText.trim(),
  optionText3: poll.optionTexts[2]?.optionText.trim(),
  optionText4: poll.optionTexts[3]?.optionText.trim(),
  optionText5: poll.optionTexts[4]?.optionText.trim()
});

export const convertClientPollToPollCreate = (
  poll?: (
    ClientPoll | ClientPollCreateRequest | ClientPollCreateForPostRequest
  ) & { id?: string }
): Poll | undefined =>
  poll
    ? {
        id: poll.id,
        text: poll.text,
        multipleChoice: poll.multipleChoice,
        expirationTime: poll.expirationTime,
        optionTexts: getPollOptions(poll).map(({ optionText }) => ({
          optionText
        }))
      }
    : undefined;

export const hasUserVoted = (votes: PollVote): boolean => {
  for (let i = 1; i <= 5; i++) {
    const votedKey = `pollOption${i}Voted` as keyof PollVote;
    if (votes[votedKey]) {
      return true;
    }
  }
  return false;
};

export const hasPollVotesChanged = (
  originalVotes: PollVote,
  currentVotes: PollVote
): boolean => {
  for (let i = 1; i <= 5; i++) {
    const votedKey = `pollOption${i}Voted` as keyof PollVote;
    const oldValue = !!originalVotes[votedKey];
    const newValue = !!currentVotes[votedKey];
    if (oldValue !== newValue) {
      return true;
    }
  }
  return false;
};

export const toPollCreateForPostRequest = (
  poll: ClientPollCreateRequest,
  postId: string
): ClientPollCreateForPostRequest => ({
  ...poll,
  postId
});
